import React, { useState, useCallback, useEffect } from 'react';
import { Message, GameState, ModelOption, StoryOption, WinCondition, GameOutcome, ReplayAction, Difficulty, AdventurePlan, GameMode, Genre, RewindState, RewindPenalty, User, SavedAdventure, MultiplayerSession } from './types';
import { startStory, continueStory, getPredictions, getStoryOptions, getRewindPenalty, getDailyChallenge, checkRateLimit } from './services/gemini';
import { loadStoredUser, getStoredToken, signOut as authSignOut, isGoogleAuthConfigured, isDiscordAuthConfigured, handleDiscordCallback, saveUser } from './services/auth';
import { MessageList } from './components/MessageList';
import { PredictiveKeyboard } from './components/PredictiveKeyboard';
import { CountdownDisplay } from './components/CountdownDisplay';
import { GoogleSignInButton } from './components/GoogleSignInButton';
import { DiscordSignInButton } from './components/DiscordSignInButton';
import { UserProfile } from './components/UserProfile';
import { Leaderboard } from './components/Leaderboard';
import { UsernameModal } from './components/UsernameModal';
import { LoginButton } from './components/LoginButton';
import { LoginModal } from './components/LoginModal';
import { SavedAdventures } from './components/SavedAdventures';
import { UsageChart } from './components/UsageChart';
import { MultiplayerLobby } from './components/MultiplayerLobby';
import { MultiplayerGame } from './components/MultiplayerGame';
import AdminPanel from './components/AdminPanel';
import { PurchasePage } from './components/PurchasePage';
import { VideoPlayer } from './components/VideoPlayer';
import { Terminal, Moon, Sun, Trophy, Skull, Sparkles, Infinity, RotateCcw, Calendar, Flame, BookOpen, Shield, Users, DollarSign } from 'lucide-react';
import { ShareButton } from './components/ShareButton';
import { submitWin, getUsername, setUsername, saveAdventure, loadAdventure } from './services/api-client';

const App: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [replayActions, setReplayActions] = useState<ReplayAction[]>([]);
  const [gameState, setGameState] = useState<GameState>(GameState.START);
  const [currentInput, setCurrentInput] = useState<string>("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isProcessingAI, setIsProcessingAI] = useState<boolean>(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState<boolean>(false);
  const [streamingText, setStreamingText] = useState<string>("");
  const [isThinking, setIsThinking] = useState<boolean>(false);
  const [customScene, setCustomScene] = useState<string>("");
  const [chatModel, setChatModel] = useState<ModelOption>('cerebras/zai-glm-4.6');
  const [predictionModel, setPredictionModel] = useState<ModelOption>('cerebras/zai-glm-4.6');
  const [error, setError] = useState<string | null>(null);
  const [storyOptions, setStoryOptions] = useState<StoryOption[]>([]);
  const [winCondition, setWinCondition] = useState<WinCondition | null>(null);
  const [adventurePlan, setAdventurePlan] = useState<AdventurePlan | null>(null);
  const [difficulty, setDifficulty] = useState<Difficulty>(2);
  const [gameOutcome, setGameOutcome] = useState<GameOutcome>(null);
  const [outcomeReason, setOutcomeReason] = useState<string>("");
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [lastFailedAction, setLastFailedAction] = useState<{type: 'continue' | 'start', data?: any} | null>(null);
  const [countdown, setCountdown] = useState<{ remaining: number; label: string } | null>(null);
  const [gameMode, setGameMode] = useState<GameMode>('adventure');
  const [genre, setGenre] = useState<Genre>('random');
  const [rewindHistory, setRewindHistory] = useState<RewindState[]>([]);
  const [rewindPenalties, setRewindPenalties] = useState<number>(0);
  const [currentPenalty, setCurrentPenalty] = useState<RewindPenalty | null>(null);
  const [isDailyChallenge, setIsDailyChallenge] = useState<boolean>(false);
  const [dailyChallengeDate, setDailyChallengeDate] = useState<string>('');
  const [showLeaderboardPrompt, setShowLeaderboardPrompt] = useState<boolean>(false);
  const [leaderboardSaved, setLeaderboardSaved] = useState<boolean>(false);
  
  // Auth state
  const [user, setUser] = useState<User | null>(() => loadStoredUser());
  const [authToken, setAuthToken] = useState<string | null>(() => getStoredToken());
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [showLeaderboard, setShowLeaderboard] = useState<boolean>(false);
  const [showUsernameModal, setShowUsernameModal] = useState<boolean>(false);
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [showSavedAdventures, setShowSavedAdventures] = useState<boolean>(false);
  const [showAdminPanel, setShowAdminPanel] = useState<boolean>(false);
  const [showPurchasePage, setShowPurchasePage] = useState<boolean>(false);
  const [currentAdventureId, setCurrentAdventureId] = useState<string | null>(null);

  // Multiplayer state
  const [multiplayerSession, setMultiplayerSession] = useState<MultiplayerSession | null>(null);

  // Usage tracking state
  const [usageData, setUsageData] = useState<{
    gamesRemaining: number;
    gamesTotal: number;
    predictionsRemaining: number;
    predictionsTotal: number;
    resetTime: number | null;
    bonusGames?: number;
    bonusPredictions?: number;
  } | null>(null);

  // Handle Sign-In (Google or Discord)
  const handleSignIn = useCallback(async (signedInUser: User, token: string) => {
    setUser(signedInUser);
    setAuthToken(token);
    setShowLoginModal(false); // Close the login modal
    
    // Check if user has a username set
    try {
      const result = await getUsername();
      if (result.username) {
        // User already has a username, update the user object
        const updatedUser = { ...signedInUser, username: result.username };
        setUser(updatedUser);
        saveUser(updatedUser);
      } else {
        // No username set, show the modal
        setShowUsernameModal(true);
      }
    } catch (err) {
      console.error('Failed to check username:', err);
      // Show modal anyway if we can't check
      setShowUsernameModal(true);
    }

    // If user came from /admin route, open admin panel after sign-in (if they're an admin)
    const wantedAdmin = sessionStorage.getItem('wantedAdmin');
    if (wantedAdmin === 'true') {
      sessionStorage.removeItem('wantedAdmin');
      // Check admin status before opening panel
      fetch('/api/admin', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }).then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Not authorized');
      }).then(data => {
        if (data.isAdmin) {
          setTimeout(() => setShowAdminPanel(true), 500);
        } else {
          alert('Access denied: You do not have admin privileges.');
        }
      }).catch(() => {
        alert('Access denied: You do not have admin privileges.');
      });
    }
  }, []);

  // Handle username submission
  const handleUsernameSubmit = useCallback(async (username: string) => {
    try {
      await setUsername(username);
      
      // Update user object with username
      if (user) {
        const updatedUser = { ...user, username };
        setUser(updatedUser);
        saveUser(updatedUser);
      }
      
      setShowUsernameModal(false);
    } catch (err) {
      console.error('Failed to set username:', err);
      alert(err instanceof Error ? err.message : 'Failed to set username. Please try again.');
    }
  }, [user]);

  // Handle Sign Out
  const handleSignOut = useCallback(() => {
    if (user) {
      authSignOut(user.email, () => {
        setUser(null);
        setAuthToken(null);
      });
    }
  }, [user]);

  // Autosave adventure when game state changes (for signed-in users)
  useEffect(() => {
    if (!user || !authToken) return;
    if (gameState !== GameState.PLAYING && gameState !== GameState.GAME_OVER) return;
    if (messages.length === 0) return;

    const saveCurrentAdventure = async () => {
      try {
        const adventureData = {
          id: currentAdventureId || undefined, // Include existing ID if available
          status: gameState === GameState.GAME_OVER ? 'completed' : 'in_progress',
          gameMode,
          difficulty,
          genre,
          isDailyChallenge,
          dailyChallengeDate: isDailyChallenge ? dailyChallengeDate : undefined,
          messages,
          replayActions,
          currentInput,
          suggestions,
          winCondition: winCondition || undefined,
          adventurePlan: adventurePlan || undefined,
          gameOutcome: gameOutcome || undefined,
          outcomeReason,
          turns: Math.ceil(messages.length / 2),
          rewindPenalties,
          countdown: countdown || undefined,
        };

        const result = await saveAdventure(adventureData);
        
        // Store the adventure ID for future updates
        if (!currentAdventureId) {
          setCurrentAdventureId(result.adventureId);
        }
      } catch (err) {
        console.error('Failed to autosave adventure:', err);
      }
    };

    // Debounce autosave
    const timeoutId = setTimeout(saveCurrentAdventure, 2000);
    return () => clearTimeout(timeoutId);
  }, [user, authToken, gameState, messages, gameMode, difficulty, genre, isDailyChallenge, 
      dailyChallengeDate, replayActions, currentInput, suggestions, winCondition, 
      adventurePlan, gameOutcome, outcomeReason, rewindPenalties, countdown, currentAdventureId]);

  // Load a saved adventure
  const handleLoadAdventure = useCallback(async (adventure: SavedAdventure) => {
    setShowSavedAdventures(false);
    
    // Restore game state
    setMessages(adventure.messages);
    setReplayActions(adventure.replayActions);
    setCurrentInput(adventure.currentInput);
    setSuggestions(adventure.suggestions);
    setGameMode(adventure.gameMode);
    setDifficulty(adventure.difficulty);
    setGenre(adventure.genre || 'random');
    setIsDailyChallenge(adventure.isDailyChallenge);
    setDailyChallengeDate(adventure.dailyChallengeDate || '');
    setWinCondition(adventure.winCondition || null);
    setAdventurePlan(adventure.adventurePlan || null);
    setGameOutcome(adventure.gameOutcome || null);
    setOutcomeReason(adventure.outcomeReason || '');
    setRewindPenalties(adventure.rewindPenalties);
    setCountdown(adventure.countdown || null);
    setCurrentAdventureId(adventure.id);
    
    // Set appropriate game state
    if (adventure.status === 'completed') {
      setGameState(GameState.GAME_OVER);
    } else {
      setGameState(GameState.PLAYING);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    // Update body background
    if (isDarkMode) {
      document.body.className = 'bg-gray-900 text-gray-100 h-screen w-screen overflow-hidden';
    } else {
      document.body.className = 'bg-gray-100 text-gray-900 h-screen w-screen overflow-hidden';
    }
  }, [isDarkMode]);

  // Check admin status when user signs in
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || !authToken) {
        setIsAdmin(false);
        return;
      }

      try {
        const response = await fetch('/api/admin', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setIsAdmin(data.isAdmin || false);
        } else {
          setIsAdmin(false);
        }
      } catch (err) {
        console.error('Failed to check admin status:', err);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user, authToken]);

  // Fetch usage data when user signs in or component mounts
  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        const data = await checkRateLimit(authToken);
        
        // Determine totals based on authentication and subscription tier
        const gamesTotal = data.isMax || data.isPro ? 999999 : (data.isAuthenticated ? 4 : 2);
        const predictionsTotal = data.isMax ? 999999 : (data.isPro ? 1600 : (data.isAuthenticated ? 200 : 100));
        
        setUsageData({
          gamesRemaining: data.remaining,
          gamesTotal,
          predictionsRemaining: data.predictions?.remaining || 0,
          predictionsTotal,
          resetTime: data.resetTime,
          bonusGames: data.bonusGames || 0,
          bonusPredictions: data.predictions?.bonusRemaining || 0,
        });

        // Update user subscription status if it changed
        if (user) {
          const needsUpdate = data.isPro !== user.isPro || data.isMax !== user.isMax;
          if (needsUpdate) {
            const updatedUser = { 
              ...user, 
              isPro: data.isPro, 
              isMax: data.isMax,
              subscriptionTier: data.isMax ? 'max' as const : (data.isPro ? 'pro' as const : 'free' as const)
            };
            setUser(updatedUser);
            saveUser(updatedUser);
          }
        }
      } catch (err) {
        console.error('Failed to fetch usage data:', err);
      }
    };

    fetchUsageData();
    
    // Refresh usage data every 30 seconds
    const interval = setInterval(fetchUsageData, 30000);
    return () => clearInterval(interval);
  }, [authToken, user]);

  // Helper function to refresh usage data
  const refreshUsageData = useCallback(async () => {
    try {
      const data = await checkRateLimit(authToken);
      const gamesTotal = data.isMax || data.isPro ? 999999 : (data.isAuthenticated ? 4 : 2);
      const predictionsTotal = data.isMax ? 999999 : (data.isPro ? 1600 : (data.isAuthenticated ? 200 : 100));
      
      setUsageData({
        gamesRemaining: data.remaining,
        gamesTotal,
        predictionsRemaining: data.predictions?.remaining || 0,
        predictionsTotal,
        resetTime: data.resetTime,
        bonusGames: data.bonusGames || 0,
        bonusPredictions: data.predictions?.bonusRemaining || 0,
      });

      // Update user subscription status if it changed
      if (user) {
        const needsUpdate = data.isPro !== user.isPro || data.isMax !== user.isMax;
        if (needsUpdate) {
          const updatedUser = { 
            ...user, 
            isPro: data.isPro, 
            isMax: data.isMax,
            subscriptionTier: data.isMax ? 'max' as const : (data.isPro ? 'pro' as const : 'free' as const)
          };
          setUser(updatedUser);
          saveUser(updatedUser);
        }
      }
    } catch (err) {
      console.error('Failed to refresh usage data:', err);
    }
  }, [authToken, user]);

  // Handle Discord OAuth callback and admin route
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const discordCode = params.get('discord_code');
    const discordState = params.get('discord_state');

    if (discordCode && discordState) {
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);

      // Handle Discord callback
      handleDiscordCallback(discordCode, discordState).then((discordUser) => {
        if (discordUser) {
          handleSignIn(discordUser, getStoredToken() || '');
        }
      });
    }

    // Check if user navigated to /admin
    if (window.location.pathname === '/admin') {
      // Clear the path but keep it in history
      window.history.replaceState({}, document.title, '/');
      
      // Open admin panel only if user is signed in and is an admin
      if (user && authToken) {
        // We'll check admin status and open panel if they're an admin
        fetch('/api/admin', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }).then(response => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Not authorized');
        }).then(data => {
          if (data.isAdmin) {
            setShowAdminPanel(true);
          } else {
            alert('Access denied: You do not have admin privileges.');
          }
        }).catch(() => {
          alert('Access denied: You do not have admin privileges.');
        });
      } else {
        // Mark that user wants admin panel, then show login
        sessionStorage.setItem('wantedAdmin', 'true');
        setShowLoginModal(true);
      }
    }
  }, [handleSignIn, user, authToken]);

  // Mode selection
  const handleSelectMode = (mode: GameMode) => {
    setGameMode(mode);
    setCurrentAdventureId(null); // Reset adventure ID for new game
    // After selecting mode, show settings screen
    setGameState(GameState.SETTINGS);
  };

  // Handle settings confirmation and start game
  const handleConfirmSettings = () => {
    if (gameMode === 'endless') {
      // Endless mode: skip story selection, go straight to game
      handleStartEndlessGame(gameMode);
    } else {
      // Adventure mode: show story options
      handleStartGame(gameMode);
    }
  };

  // Start Daily Challenge
  const handleStartDailyChallenge = async () => {
    const today = new Date();
    
    setIsDailyChallenge(true);
    setDailyChallengeDate(today.toISOString().split('T')[0]);
    setGameMode('adventure');
    setGameState(GameState.LOADING_INITIAL);
    setMessages([]);
    setReplayActions([]);
    setRewindHistory([]);
    setRewindPenalties(0);
    setCurrentPenalty(null);
    setCurrentInput("");
    setStreamingText("");
    setError(null);
    setStoryOptions([]);
    setWinCondition(null);
    setAdventurePlan(null);
    setGameOutcome(null);
    setOutcomeReason("");
    setCountdown(null);
    
    try {
      // Check rate limit BEFORE generating challenge
      const rateLimitCheck = await checkRateLimit(authToken);
      if (!rateLimitCheck.allowed) {
        setError(`🚫 ${rateLimitCheck.message}`);
        setIsDailyChallenge(false);
        setGameState(GameState.CHOOSING_MODE);
        return;
      }
      
      const challenge = await getDailyChallenge(chatModel);
      setDifficulty(challenge.difficulty);
      setGenre(challenge.genre);
      
      // Daily challenge has only 1 option, select it directly
      if (challenge.options && challenge.options.length > 0) {
        await handleSelectStory(challenge.options[0]);
      } else {
        throw new Error('No scenario generated for daily challenge');
      }
    } catch (err) {
      console.error('Failed to get daily challenge:', err);
      const errorMsg = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to load daily challenge: ${errorMsg}`);
      setIsDailyChallenge(false);
      setGameState(GameState.CHOOSING_MODE);
    }
  };

  // Start endless game directly
  const handleStartEndlessGame = async (mode: GameMode) => {
    setGameState(GameState.LOADING_INITIAL);
    setMessages([]);
    setReplayActions([]);
    setRewindHistory([]);
    setRewindPenalties(0);
    setCurrentPenalty(null);
    setCurrentInput("");
    setStreamingText("");
    setError(null);
    setStoryOptions([]);
    setWinCondition(null);
    setAdventurePlan(null);
    setGameOutcome(null);
    setOutcomeReason("");
    setCountdown(null);
    setIsDailyChallenge(false);
    
    try {
      // Check rate limit BEFORE generating options
      const rateLimitCheck = await checkRateLimit(authToken);
      if (!rateLimitCheck.allowed) {
        setError(`🚫 ${rateLimitCheck.message}`);
        setGameState(GameState.CHOOSING_MODE);
        return;
      }
      
      // For endless mode, just pick the first option (or generate one)
      const options = await getStoryOptions(customScene || undefined, chatModel, difficulty, mode, genre);
      if (options.length > 0) {
        await handleSelectStory(options[0]);
      }
    } catch (err) {
      console.error('Failed to start endless game:', err);
      const errorMsg = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to start endless game: ${errorMsg}`);
      setGameState(GameState.CHOOSING_MODE);
    }
  };

  // Initial Game Start - now generates 3 options (for adventure mode)
  const handleStartGame = async (mode: GameMode) => {
    setGameState(GameState.LOADING_INITIAL);
    setMessages([]);
    setReplayActions([]);
    setRewindHistory([]);
    setRewindPenalties(0);
    setCurrentPenalty(null);
    setCurrentInput("");
    setStreamingText("");
    setError(null);
    setStoryOptions([]);
    setWinCondition(null);
    setAdventurePlan(null);
    setGameOutcome(null);
    setOutcomeReason("");
    setCountdown(null);
    setIsDailyChallenge(false);
    
    try {
      // Check rate limit BEFORE generating options
      const rateLimitCheck = await checkRateLimit(authToken);
      if (!rateLimitCheck.allowed) {
        setError(`🚫 ${rateLimitCheck.message}`);
        setGameState(GameState.CHOOSING_MODE);
        return;
      }
      
      // Get 3 story options with difficulty, mode, and genre
      const options = await getStoryOptions(customScene || undefined, chatModel, difficulty, mode, genre);
      setStoryOptions(options);
      setGameState(GameState.CHOOSING_STORY);
    } catch (err) {
      console.error('Failed to get story options:', err);
      const errorMsg = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to generate story options: ${errorMsg}`);
      setGameState(GameState.CHOOSING_MODE);
    }
  };

  // Handle selecting a story option
  const handleSelectStory = async (option: StoryOption) => {
    setGameState(GameState.LOADING_INITIAL);
    setStoryOptions([]); // Clear options - AI won't remember the others
    setStreamingText("");
    setError(null);
    setLastFailedAction(null);
    
    // Store the win condition for this story
    if (option.winCondition) {
      setWinCondition(option.winCondition);
    }
    
    // Store the adventure plan
    if (option.adventurePlan) {
      setAdventurePlan(option.adventurePlan);
    }
    
    try {
      // Start the story with the selected option's full prompt
      const introText = await startStory(
        (chunk) => {
          setStreamingText((prev) => prev + chunk);
        }, 
        option.fullPrompt, 
        chatModel,
        (thinking) => {
          setIsThinking(thinking);
        },
        0,
        authToken
      );
      
      setStreamingText("");
      setIsThinking(false);
      
      // Check for rate limit error
      if (introText.includes('🚫')) {
        setError(introText);
        setLastFailedAction({ type: 'start', data: option });
        setGameState(GameState.CHOOSING_STORY);
        return;
      }
      
      if (introText.includes('(API Error)') || introText.includes('(Stream Error)') || 
          introText.includes('(Timeout Error)') || introText.includes('(API Key Error)')) {
        setError(`Failed to start story: ${introText}`);
        setLastFailedAction({ type: 'start', data: option });
        setGameState(GameState.CHOOSING_STORY);
        return;
      }
      
      const introMsg: Message = {
        id: Date.now().toString(),
        text: introText,
        sender: 'ai',
        timestamp: Date.now()
      };
      const initialMessages = [introMsg];
      setMessages(initialMessages);
      setReplayActions([{ type: 'message', message: introMsg, timestamp: Date.now() }]);
      setGameState(GameState.PLAYING);

      // Refresh usage data after successfully starting a game
      refreshUsageData();

      loadSuggestions(initialMessages, "");
    } catch (err) {
      console.error('Failed to start selected story:', err);
      setError('Failed to start the adventure. Please try again.');
      setLastFailedAction({ type: 'start', data: option });
      setGameState(GameState.CHOOSING_STORY);
    }
  };

  const loadSuggestions = async (msgs: Message[], current: string) => {
    setIsLoadingSuggestions(true);
    try {
      // Pass the full message history to get context-aware predictions
      const newSuggestions = await getPredictions(msgs, current, predictionModel, authToken);
      setSuggestions(newSuggestions);
      
      // Refresh usage data after getting predictions (counts against limit)
      refreshUsageData();
    } catch (e) {
      setSuggestions(["Err", "System", "Fail"]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleSelectSuggestion = useCallback((word: string) => {
    // Track word selection for replay
    setReplayActions(prev => [...prev, { type: 'word', word, timestamp: Date.now() }]);
    
    // Append word to input
    setCurrentInput(prev => {
      const spacing = prev.length > 0 && !prev.endsWith(" ") ? " " : "";
      const newInput = prev + spacing + word;
      
      // Trigger new suggestions with the UPDATED input and current messages
      // Note: We use the 'messages' from closure, which is updated via dependency
      loadSuggestions(messages, newInput);
      
      return newInput;
    });
  }, [messages]);

  const handleBackspace = useCallback(() => {
    setCurrentInput(prev => {
      // Remove last word
      const words = prev.trim().split(" ");
      if (words.length <= 1) return "";
      words.pop();
      const newInput = words.join(" ");

       // Trigger new suggestions based on new state
       loadSuggestions(messages, newInput);

       return newInput;
    });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!currentInput.trim()) return;

    // Save current state for potential rewind (before making changes)
    const rewindState: RewindState = {
      messages: [...messages],
      replayActions: [...replayActions],
      currentInput: currentInput,
      suggestions: [...suggestions],
      countdown: countdown ? { ...countdown } : null,
      penaltiesApplied: rewindPenalties,
    };
    setRewindHistory(prev => [...prev, rewindState]);

    const userMsg: Message = {
      id: Date.now().toString(),
      text: currentInput,
      sender: 'user',
      timestamp: Date.now()
    };

    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setReplayActions(prev => [...prev, { type: 'message', message: userMsg, timestamp: Date.now() }]);
    const actionToSend = currentInput;
    setCurrentInput("");
    setSuggestions(["...", "...", "..."]); // Clear suggestions while thinking
    setIsProcessingAI(true);
    setStreamingText("");
    setError(null);
    setLastFailedAction(null);
    setCurrentPenalty(null); // Clear any displayed penalty

    try {
      // Get AI response with streaming, passing win condition for evaluation
      const response = await continueStory(
        updatedMessages, 
        actionToSend, 
        (chunk) => {
          setStreamingText((prev) => prev + chunk);
        }, 
        chatModel,
        winCondition || undefined,
        (thinking) => {
          setIsThinking(thinking);
        },
        difficulty,
        adventurePlan || undefined
      );
      
      setStreamingText("");
      setIsThinking(false);
      
      // Check if we got an error message and show the actual error details
      if (response.text.includes('(API Error)') || response.text.includes('(Stream Error)') || 
          response.text.includes('(Timeout Error)') || response.text.includes('(API Key Error)')) {
        setError(`Story continuation failed: ${response.text}`);
        setLastFailedAction({ type: 'continue', data: { messages: updatedMessages, action: actionToSend } });
        setIsProcessingAI(false);
        loadSuggestions(updatedMessages, "");
        return;
      }
      
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: response.text,
        sender: 'ai',
        timestamp: Date.now()
      };

      const finalMessages = [...updatedMessages, aiMsg];
      setMessages(finalMessages);
      setReplayActions(prev => [...prev, { type: 'message', message: aiMsg, timestamp: Date.now() }]);
      setIsProcessingAI(false);

      // Handle countdown updates
      if (response.countdown) {
        if (response.countdown.action === 'start' && response.countdown.remaining !== undefined) {
          setCountdown({ remaining: response.countdown.remaining, label: response.countdown.label || 'Moves remaining' });
        } else if (response.countdown.action === 'decrement' && countdown) {
          const newRemaining = countdown.remaining - 1;
          if (newRemaining <= 0) {
            // Countdown reached zero - player loses
            setGameOutcome('lose');
            setOutcomeReason(response.countdown.label ? `Time's up! ${response.countdown.label}` : 'You ran out of time!');
            setGameState(GameState.GAME_OVER);
            setCountdown(null);
            return;
          } else {
            setCountdown({ ...countdown, remaining: newRemaining });
          }
        } else if (response.countdown.action === 'cancel') {
          setCountdown(null);
        }
      }

      // Check for game outcome
      if (response.outcome) {
        setGameOutcome(response.outcome);
        setOutcomeReason(response.outcomeReason || '');
        setGameState(GameState.GAME_OVER);
        setCountdown(null);
        
        // Show leaderboard prompt if user is signed in and game was won
        if (user && (response.outcome === 'win' || response.outcome === 'alternative_win')) {
          setShowLeaderboardPrompt(true);
          setLeaderboardSaved(false);
        }
      } else {
        // Get new predictions for the *next* turn start (empty input)
        loadSuggestions(finalMessages, "");
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to continue the story. Please try again.');
      setLastFailedAction({ type: 'continue', data: { messages: updatedMessages, action: actionToSend } });
      setIsProcessingAI(false);
      // Restore suggestions for retry
      loadSuggestions(updatedMessages, "");
    }
  };

  // Retry last failed action
  const handleRetry = async () => {
    if (!lastFailedAction) return;
    
    if (lastFailedAction.type === 'start') {
      await handleSelectStory(lastFailedAction.data);
    } else if (lastFailedAction.type === 'continue') {
      const { messages: prevMessages, action } = lastFailedAction.data;
      setMessages(prevMessages);
      setIsProcessingAI(true);
      setStreamingText("");
      setError(null);
      setLastFailedAction(null);
      
      try {
        const response = await continueStory(
          prevMessages, 
          action, 
          (chunk) => {
            setStreamingText((prev) => prev + chunk);
          }, 
          chatModel,
          winCondition || undefined,
          (thinking) => {
            setIsThinking(thinking);
          },
          difficulty,
          adventurePlan || undefined
        );
        
        setStreamingText("");
        setIsThinking(false);
        
        if (response.text.includes('(API Error)') || response.text.includes('(Stream Error)') || 
            response.text.includes('(Timeout Error)') || response.text.includes('(API Key Error)')) {
          setError(`Story continuation failed: ${response.text}`);
          setLastFailedAction({ type: 'continue', data: { messages: prevMessages, action } });
          setIsProcessingAI(false);
          return;
        }
        
        const aiMsg: Message = {
          id: (Date.now() + 1).toString(),
          text: response.text,
          sender: 'ai',
          timestamp: Date.now()
        };

        const finalMessages = [...prevMessages, aiMsg];
        setMessages(finalMessages);
        setReplayActions(prev => [...prev, { type: 'message', message: aiMsg, timestamp: Date.now() }]);
        setIsProcessingAI(false);

        if (response.outcome) {
          setGameOutcome(response.outcome);
          setOutcomeReason(response.outcomeReason || '');
          setGameState(GameState.GAME_OVER);
          setCountdown(null);
          
          // Show leaderboard prompt if user is signed in and game was won
          if (user && (response.outcome === 'win' || response.outcome === 'alternative_win')) {
            setShowLeaderboardPrompt(true);
            setLeaderboardSaved(false);
          }
        } else {
          loadSuggestions(finalMessages, "");
        }
      } catch (err) {
        console.error('Retry failed:', err);
        setError('Retry failed. Please try again.');
        setLastFailedAction({ type: 'continue', data: { messages: prevMessages, action } });
        setIsProcessingAI(false);
      }
    }
  };

  // Rewind to previous state with a funny penalty
  const handleRewind = () => {
    if (rewindHistory.length === 0 || isProcessingAI) return;
    
    // Get the last saved state
    const previousState = rewindHistory[rewindHistory.length - 1];
    
    // Apply the rewind
    setMessages(previousState.messages);
    setReplayActions(previousState.replayActions);
    setCurrentInput(previousState.currentInput);
    setSuggestions(previousState.suggestions);
    setCountdown(previousState.countdown);
    
    // Remove the used rewind state
    setRewindHistory(prev => prev.slice(0, -1));
    
    // Apply a penalty
    const penalty = getRewindPenalty();
    setCurrentPenalty(penalty);
    setRewindPenalties(prev => prev + 1);
    
    // Clear any errors
    setError(null);
    setLastFailedAction(null);
  };

  // Save win to leaderboard
  const handleSaveToLeaderboard = async () => {
    if (!user || leaderboardSaved) return;
    
    try {
      await submitWin({
        difficulty,
        gameMode,
        turns: Math.ceil(messages.length / 2),
        rewindPenalties,
        isDailyChallenge,
        dailyChallengeDate: isDailyChallenge ? dailyChallengeDate : undefined,
      });
      setLeaderboardSaved(true);
      setShowLeaderboardPrompt(false);
    } catch (err) {
      console.error('Failed to submit win to leaderboard:', err);
      
      // Check if it's a session expiration error
      if (err instanceof Error && err.message.includes('session has expired')) {
        setError('Your session has expired. Please sign in again to save your score.');
        // Clear user state to show sign-in button
        setUser(null);
        setAuthToken(null);
      } else {
        setError('Failed to save to leaderboard. Please try again.');
      }
    }
  };

  const handleSkipLeaderboard = () => {
    setShowLeaderboardPrompt(false);
  };

  // Main Render
  return (
    <>
      {/* Top Right Controls - Outside Phone Container */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        {/* Share Button - only show when there are messages */}
        {messages.length > 0 && (
          <ShareButton 
            messages={messages} 
            replayActions={replayActions}
            isDarkMode={isDarkMode}
            gameOutcome={gameOutcome}
            outcomeReason={outcomeReason}
          />
        )}
        {/* Login Button - only show when not signed in */}
        {!user && (isGoogleAuthConfigured() || isDiscordAuthConfigured()) && (
          <LoginButton onClick={() => setShowLoginModal(true)} isDarkMode={isDarkMode} />
        )}
        {/* User Profile - only show when signed in */}
        {user && (
          <div className="hidden sm:block">
            <UserProfile 
              user={user} 
              onSignOut={handleSignOut} 
              isDarkMode={isDarkMode} 
            />
          </div>
        )}
        <button
          onClick={() => setIsDarkMode(!isDarkMode)}
          className={`p-2 rounded-full transition-colors ${
            isDarkMode 
              ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' 
              : 'bg-white text-gray-700 hover:bg-gray-100'
          } shadow-lg`}
          aria-label="Toggle dark mode"
        >
          {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </div>

      {/* Phone Container */}
      <div className={`flex flex-col h-full overflow-hidden relative transition-all duration-500 ${
        gameState === GameState.START || gameState === GameState.CHOOSING_MODE || gameState === GameState.SETTINGS || gameState === GameState.CHOOSING_STORY
          ? 'w-full'
          : 'max-w-md mx-auto shadow-2xl'
      } ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      
      {/* Error Banner */}
      {error && (
        <div className={`border-l-4 border-red-500 p-3 text-sm flex items-center justify-between ${
          isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'
        }`}>
          <span className="flex-1">{error}</span>
          <div className="flex items-center gap-2 ml-4">
            {lastFailedAction && (
              <button 
                onClick={handleRetry}
                className={`px-3 py-1 rounded font-medium transition-colors ${
                  isDarkMode 
                    ? 'bg-red-800 hover:bg-red-700 text-red-200' 
                    : 'bg-red-200 hover:bg-red-300 text-red-800'
                }`}
              >
                Retry
              </button>
            )}
            <button 
              onClick={() => setError(null)}
              className={`font-bold ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'}`}
            >
              ✕
            </button>
          </div>
        </div>
      )}
      
      {/* Game Content */}
      {gameState === GameState.MULTIPLAYER_LOBBY && user ? (
        <MultiplayerLobby
          user={user}
          onGameStart={(session) => {
            setMultiplayerSession(session);
            setGameState(GameState.MULTIPLAYER_WAITING);
          }}
          onBack={() => setGameState(GameState.CHOOSING_MODE)}
          isDarkMode={isDarkMode}
        />
      ) : gameState === GameState.MULTIPLAYER_WAITING && multiplayerSession && user ? (
        <MultiplayerGame
          session={multiplayerSession}
          user={user}
          onGameOver={(outcome, reason) => {
            setGameOutcome(outcome);
            setOutcomeReason(reason);
            setMultiplayerSession(null);
            setGameState(GameState.GAME_OVER);
          }}
          onLeave={() => {
            setMultiplayerSession(null);
            setGameState(GameState.CHOOSING_MODE);
          }}
          isDarkMode={isDarkMode}
          authToken={authToken}
        />
      ) : gameState === GameState.CHOOSING_STORY ? (
        <div className={`flex-1 flex flex-col p-4 overflow-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <h2 className={`text-xl font-bold mb-2 text-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            Choose Your Adventure
          </h2>
          <p className={`text-sm mb-1 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Pick one to begin your journey
          </p>
          <p className={`text-xs mb-4 text-center ${
            difficulty === 1 
              ? (isDarkMode ? 'text-green-400' : 'text-green-600')
              : difficulty === 2 
              ? (isDarkMode ? 'text-yellow-400' : 'text-yellow-600')
              : difficulty === 3
              ? (isDarkMode ? 'text-red-400' : 'text-red-600')
              : (isDarkMode ? 'text-purple-400' : 'text-purple-600')
          }`}>
            {difficulty === 1 ? '🟢 Easy Mode' : difficulty === 2 ? '🟡 Medium Mode' : difficulty === 3 ? '🔴 Hard Mode' : '💀 PURE CHAOS MODE'}
            {isDailyChallenge && ` • 📅 Daily Challenge (${dailyChallengeDate})`}
            {genre !== 'random' && ` • ${genre.charAt(0).toUpperCase() + genre.slice(1)}`}
          </p>
          <div className="flex gap-3 flex-1 min-h-0">
            {storyOptions.map((option, idx) => (
              <button
                key={option.id}
                onClick={() => handleSelectStory(option)}
                className={`flex-1 p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.02] active:scale-[0.98] flex flex-col ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 hover:border-blue-500 hover:bg-gray-750' 
                    : 'bg-gray-50 border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <span className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {idx === 0 ? 'Option A' : idx === 1 ? 'Option B' : 'Option C'}
                </span>
                <p className={`text-sm leading-relaxed flex-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  {option.preview}
                </p>
                {option.adventurePlan && (
                  <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      ~{option.adventurePlan.estimatedTurns} turns • {option.adventurePlan.alternatePaths?.length || 0} alternate paths
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
          <button
            onClick={() => setGameState(GameState.CHOOSING_MODE)}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isDarkMode 
                ? 'text-gray-400 hover:text-gray-200' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            ← Back
          </button>
        </div>
      ) : gameState === GameState.CHOOSING_MODE ? (
        <div className={`flex-1 flex flex-col p-4 overflow-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <h2 className={`text-xl font-bold mb-2 text-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            Choose Game Mode
          </h2>
          <p className={`text-sm mb-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            How do you want to play?
          </p>
          
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Adventure Mode */}
            <button
              onClick={() => handleSelectMode('adventure')}
              className={`flex-1 p-6 rounded-xl border-2 text-left transition-all hover:scale-[1.02] active:scale-[0.98] flex flex-col ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 hover:border-blue-500 hover:bg-gray-750' 
                  : 'bg-gray-50 border-gray-200 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <Trophy size={32} className={isDarkMode ? 'text-yellow-400' : 'text-yellow-600'} />
                <div>
                  <h3 className={`text-lg font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Adventure Mode
                  </h3>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Classic goal-based gameplay
                  </span>
                </div>
              </div>
              <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Complete objectives to win. Face challenges, solve puzzles, and achieve your goal. Can win or lose based on your actions.
              </p>
            </button>

            {/* Endless Mode */}
            <button
              onClick={() => handleSelectMode('endless')}
              className={`flex-1 p-6 rounded-xl border-2 text-left transition-all hover:scale-[1.02] active:scale-[0.98] flex flex-col ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 hover:border-purple-500 hover:bg-gray-750' 
                  : 'bg-gray-50 border-gray-200 hover:border-purple-400 hover:bg-purple-50'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <Infinity size={32} className={isDarkMode ? 'text-purple-400' : 'text-purple-600'} />
                <div>
                  <h3 className={`text-lg font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Endless Mode
                  </h3>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No win condition, just chaos
                  </span>
                </div>
              </div>
              <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Start a conversation and see where it goes. No objectives, no time limits. Keep playing until something goes hilariously wrong.
              </p>
            </button>

            {/* Multiplayer Mode - Only show for logged-in users */}
            {user && (
              <button
                onClick={() => setGameState(GameState.MULTIPLAYER_LOBBY)}
                className={`flex-1 p-6 rounded-xl border-2 text-left transition-all hover:scale-[1.02] active:scale-[0.98] flex flex-col ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 hover:border-green-500 hover:bg-gray-750' 
                    : 'bg-gray-50 border-gray-200 hover:border-green-400 hover:bg-green-50'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <Users size={32} className={isDarkMode ? 'text-green-400' : 'text-green-600'} />
                  <div>
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                      Multiplayer
                    </h3>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Play with a friend in real-time
                    </span>
                  </div>
                </div>
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Take turns choosing words with a friend. Two modes: swap on each message, or swap on each word selection.
                </p>
              </button>
            )}
          </div>
          
          <button
            onClick={() => setGameState(GameState.START)}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isDarkMode 
                ? 'text-gray-400 hover:text-gray-200' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            ← Back
          </button>
        </div>
      ) : gameState === GameState.SETTINGS ? (
        <div className={`flex-1 flex flex-col p-4 overflow-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <h2 className={`text-xl font-bold mb-2 text-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            Game Settings
          </h2>
          <p className={`text-sm mb-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Customize your adventure
          </p>
          
          <div className="space-y-6 flex-1">
            {/* Difficulty */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Difficulty
                </span>
                <span className={`text-xs px-2 py-1 rounded ${
                  difficulty === 1 
                    ? (isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700')
                    : difficulty === 2 
                    ? (isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-700')
                    : difficulty === 3
                    ? (isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700')
                    : (isDarkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-700')
                }`}>
                  {difficulty === 1 ? 'Easy' : difficulty === 2 ? 'Medium' : difficulty === 3 ? 'Hard' : 'Chaos'}
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="4"
                value={difficulty}
                onChange={(e) => setDifficulty(Number(e.target.value) as Difficulty)}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: isDarkMode 
                    ? `linear-gradient(to right, #22c55e 0%, #eab308 33%, #ef4444 66%, #a855f7 100%)`
                    : `linear-gradient(to right, #86efac 0%, #fde047 33%, #fca5a5 66%, #d8b4fe 100%)`
                }}
              />
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {difficulty === 1 && 'Easier objectives, more forgiving AI'}
                {difficulty === 2 && 'Balanced challenge with fair objectives'}
                {difficulty === 3 && 'Harder objectives, stricter AI interpretation'}
                {difficulty === 4 && 'Pure chaos! Anything can happen'}
              </p>
            </div>
            
            {/* Genre */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Genre
              </label>
              <select
                value={genre}
                onChange={(e) => setGenre(e.target.value as Genre)}
                className={`w-full border px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-200' 
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
              >
                <option value="random">🎲 Random</option>
                <option value="fantasy">🧙 Fantasy</option>
                <option value="sci-fi">🚀 Sci-Fi</option>
                <option value="horror">👻 Horror</option>
                <option value="comedy">😂 Comedy</option>
                <option value="mystery">🔍 Mystery</option>
                <option value="romance">💕 Romance</option>
                <option value="western">🤠 Western</option>
                <option value="post-apocalyptic">☢️ Post-Apocalyptic</option>
              </select>
            </div>

            {/* Custom Scene */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Custom Scene (optional)
              </label>
              <textarea
                value={customScene}
                onChange={(e) => setCustomScene(e.target.value)}
                placeholder="e.g., 'a spaceship landing on Mars'"
                className={`w-full h-20 px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
                maxLength={200}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {customScene.length}/200 characters
              </p>
            </div>
          </div>
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setGameState(GameState.CHOOSING_MODE)}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isDarkMode 
                  ? 'text-gray-400 hover:text-gray-200 border border-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 border border-gray-300'
              }`}
            >
              ← Back
            </button>
            <button
              onClick={handleConfirmSettings}
              className={`flex-1 px-6 py-2 rounded-lg font-bold text-sm shadow-lg active:scale-95 transition-all ${
                isDarkMode 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-slate-900 text-white hover:bg-slate-800'
              }`}
            >
              Continue →
            </button>
          </div>
        </div>
      ) : gameState === GameState.GAME_OVER ? (
        <div className={`flex-1 flex flex-col items-center justify-center p-8 text-center ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className={`mb-6 p-6 rounded-full ${
              gameOutcome === 'win' 
                ? (isDarkMode ? 'bg-yellow-900 text-yellow-400' : 'bg-yellow-100 text-yellow-600')
                : gameOutcome === 'alternative_win'
                ? (isDarkMode ? 'bg-purple-900 text-purple-400' : 'bg-purple-100 text-purple-600')
                : (isDarkMode ? 'bg-red-900 text-red-400' : 'bg-red-100 text-red-600')
            }`}>
                {gameOutcome === 'win' ? <Trophy size={64} /> : gameOutcome === 'alternative_win' ? <Sparkles size={64} /> : <Skull size={64} />}
            </div>
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
              {gameOutcome === 'win' ? 'Victory!' : gameOutcome === 'alternative_win' ? 'Unexpected Victory!' : 'Game Over'}
            </h2>
            <p className={`mb-4 leading-relaxed max-w-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {outcomeReason}
            </p>
            {winCondition && (
              <p className={`mb-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Original objective: {winCondition.objective}
              </p>
            )}
            {/* Stats */}
            <div className={`mb-6 flex gap-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {rewindPenalties > 0 && (
                <span className="flex items-center gap-1">
                  <RotateCcw size={14} />
                  {rewindPenalties} rewind{rewindPenalties > 1 ? 's' : ''}
                </span>
              )}
              {isDailyChallenge && (
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  Daily Challenge
                </span>
              )}
              <span>{Math.ceil(messages.length / 2)} turns</span>
            </div>

            {/* Leaderboard Prompt - Only show for wins when user is signed in */}
            {showLeaderboardPrompt && user && (gameOutcome === 'win' || gameOutcome === 'alternative_win') && (
              <div className={`mb-6 p-4 rounded-xl border-2 w-full max-w-xs ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Save your score to the leaderboard?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveToLeaderboard}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                      isDarkMode 
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    Save Score
                  </button>
                  <button
                    onClick={handleSkipLeaderboard}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}

            {/* Success message after saving */}
            {leaderboardSaved && (
              <div className={`mb-4 p-3 rounded-lg ${
                isDarkMode ? 'bg-green-900 text-green-400' : 'bg-green-100 text-green-700'
              }`}>
                <p className="text-sm">Score saved to leaderboard!</p>
              </div>
            )}

            {/* Sign in prompt for non-authenticated users who won */}
            {!user && (gameOutcome === 'win' || gameOutcome === 'alternative_win') && (isGoogleAuthConfigured() || isDiscordAuthConfigured()) && (
              <div className={`mb-6 p-4 rounded-xl border-2 w-full max-w-xs ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Sign in to save your score to the leaderboard
                </p>
                <div className="space-y-2">
                  {isGoogleAuthConfigured() && (
                    <GoogleSignInButton onSignIn={handleSignIn} isDarkMode={isDarkMode} />
                  )}
                  {isDiscordAuthConfigured() && (
                    <DiscordSignInButton isDarkMode={isDarkMode} />
                  )}
                </div>
              </div>
            )}

            <button
                onClick={() => {
                  setIsDailyChallenge(false);
                  setGameState(GameState.START);
                  setShowLeaderboardPrompt(false);
                  setLeaderboardSaved(false);
                  setCurrentAdventureId(null);
                }}
                className={`px-8 py-4 rounded-xl font-bold text-lg shadow-lg active:scale-95 transition-all w-full max-w-xs ${
                  isDarkMode 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
            >
                Play Again
            </button>

            {/* View Adventures Button - Only show when signed in */}
            {user && (
              <button
                  onClick={() => setShowSavedAdventures(true)}
                  className={`mt-2 px-6 py-2 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 w-full max-w-xs ${
                    isDarkMode 
                      ? 'text-gray-400 hover:text-gray-200' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                  <BookOpen size={16} />
                  <span>View My Adventures</span>
              </button>
            )}
        </div>
      ) : gameState === GameState.START ? (
        <div className={`flex-1 flex flex-col items-center p-8 text-center overflow-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className="w-full max-w-4xl mx-auto space-y-8 py-8">
                {/* Hero Section */}
                <div className="space-y-4">
                    <div className={`inline-flex p-6 rounded-full ${isDarkMode ? 'bg-blue-900 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                        <Terminal size={48} />
                    </div>
                    <h1 className={`text-4xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                        Autocorrect Adventure
                    </h1>
                    <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Navigate stories using only AI-suggested words
                    </p>
                    <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        <p>Pick from three AI-generated word suggestions at a time. Watch as your chaotic choices create hilarious, unpredictable stories.</p>
                    </div>
                </div>

                {/* Main Action Buttons */}
                <div className="space-y-3">
                    {/* User Profile */}
                    {user && (
                      <UserProfile 
                        user={user} 
                        onSignOut={handleSignOut} 
                        isDarkMode={isDarkMode} 
                      />
                    )}

                    <button
                        onClick={() => setGameState(GameState.CHOOSING_MODE)}
                        className={`w-full px-6 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl active:scale-95 transition-all ${
                          isDarkMode 
                            ? 'bg-blue-600 text-white hover:bg-blue-700' 
                            : 'bg-slate-900 text-white hover:bg-slate-800'
                        }`}
                    >
                        Start Adventure
                    </button>

                    <button
                        onClick={handleStartDailyChallenge}
                        className={`w-full px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                          isDarkMode 
                            ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white hover:from-orange-500 hover:to-red-500' 
                            : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-400 hover:to-red-400'
                        } shadow-md hover:shadow-lg active:scale-95`}
                    >
                        <Calendar size={16} />
                        <span>Daily Challenge</span>
                    </button>

                    <button
                        onClick={() => setShowLeaderboard(true)}
                        className={`w-full px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                          isDarkMode 
                            ? 'bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700' 
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        } shadow-sm hover:shadow-md active:scale-95`}
                    >
                        <Trophy size={16} />
                        <span>Leaderboard</span>
                    </button>

                    {/* My Adventures Button - Only show when signed in */}
                    {user && (
                      <button
                          onClick={() => setShowSavedAdventures(true)}
                          className={`w-full px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                            isDarkMode 
                              ? 'bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700' 
                              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                          } shadow-sm hover:shadow-md active:scale-95`}
                      >
                          <BookOpen size={16} />
                          <span>My Adventures</span>
                      </button>
                    )}

                    {/* Purchase/Upgrade Button */}
                    <button
                        onClick={() => setShowPurchasePage(true)}
                        className={`w-full px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                          isDarkMode 
                            ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500' 
                            : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-400 hover:to-emerald-400'
                        } shadow-sm hover:shadow-md active:scale-95`}
                    >
                        <DollarSign size={16} />
                        <span>Upgrade / Promo Code</span>
                    </button>

                    {/* Admin Panel Button - Only show for admins */}
                    {user && authToken && isAdmin && (
                      <button
                          onClick={() => setShowAdminPanel(true)}
                          className={`w-full px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                            isDarkMode 
                              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500' 
                              : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-400 hover:to-purple-400'
                          } shadow-sm hover:shadow-md active:scale-95`}
                      >
                          <Shield size={16} />
                          <span>Admin Panel</span>
                      </button>
                    )}
                </div>

                {/* Usage Chart - Show for logged in users */}
                {user && usageData && (
                  <UsageChart
                    gamesRemaining={usageData.gamesRemaining}
                    gamesTotal={usageData.gamesTotal}
                    predictionsRemaining={usageData.predictionsRemaining}
                    predictionsTotal={usageData.predictionsTotal}
                    isDarkMode={isDarkMode}
                    resetTime={usageData.resetTime}
                    bonusGames={usageData.bonusGames}
                    bonusPredictions={usageData.bonusPredictions}
                  />
                )}

                {/* Video Player - Show only for non-logged-in users */}
                {!user && (
                  <VideoPlayer isDarkMode={isDarkMode} />
                )}
            </div>
        </div>
      ) : (
        <>
            {/* Win Condition Hint Banner - Only show in Adventure Mode */}
            {winCondition && gameState === GameState.PLAYING && gameMode === 'adventure' && (
              <div className={`px-4 py-2 text-xs flex items-center gap-2 border-b ${
                isDarkMode 
                  ? 'bg-gray-800 text-gray-400 border-gray-700' 
                  : 'bg-gray-50 text-gray-500 border-gray-200'
              }`}>
                <Trophy size={14} className={isDarkMode ? 'text-yellow-500' : 'text-yellow-600'} />
                <span className="flex-1">Hint: {winCondition.hint}</span>
                {/* Rewind Button */}
                {rewindHistory.length > 0 && !isProcessingAI && (
                  <button
                    onClick={handleRewind}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    title="Rewind (with penalty)"
                  >
                    <RotateCcw size={12} />
                    <span>Rewind</span>
                    {rewindPenalties > 0 && <span className="text-red-400">({rewindPenalties})</span>}
                  </button>
                )}
              </div>
            )}
            
            {/* Endless Mode Indicator */}
            {gameState === GameState.PLAYING && gameMode === 'endless' && (
              <div className={`px-4 py-2 text-xs flex items-center gap-2 border-b ${
                isDarkMode 
                  ? 'bg-gray-800 text-gray-400 border-gray-700' 
                  : 'bg-gray-50 text-gray-500 border-gray-200'
              }`}>
                <Infinity size={14} className={isDarkMode ? 'text-purple-500' : 'text-purple-600'} />
                <span className="flex-1">Endless Mode: No win condition, just keep the conversation going!</span>
                {/* Rewind Button for Endless Mode */}
                {rewindHistory.length > 0 && !isProcessingAI && (
                  <button
                    onClick={handleRewind}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    title="Rewind (with penalty)"
                  >
                    <RotateCcw size={12} />
                    <span>Rewind</span>
                    {rewindPenalties > 0 && <span className="text-red-400">({rewindPenalties})</span>}
                  </button>
                )}
              </div>
            )}
            
            {/* Rewind Penalty Display */}
            {currentPenalty && gameState === GameState.PLAYING && (
              <div className={`px-4 py-2 text-xs border-b animate-pulse ${
                isDarkMode 
                  ? 'bg-purple-900 text-purple-200 border-purple-700' 
                  : 'bg-purple-100 text-purple-700 border-purple-200'
              }`}>
                {currentPenalty.type === 'narrator_sass' && (
                  <span>🎭 {currentPenalty.message}</span>
                )}
                {currentPenalty.type === 'chaos_suggestion' && (
                  <span>⚡ Penalty: {currentPenalty.description}</span>
                )}
                {currentPenalty.type === 'word_ban' && (
                  <span>🚫 Penalty: The word "{currentPenalty.bannedWord}" has been temporarily banned from your vocabulary!</span>
                )}
                {currentPenalty.type === 'item_loss' && (
                  <span>💔 Penalty: You lost {currentPenalty.item}!</span>
                )}
              </div>
            )}
            
            {/* Daily Challenge Banner */}
            {isDailyChallenge && gameState === GameState.PLAYING && (
              <div className={`px-4 py-1 text-xs flex items-center justify-center gap-2 border-b ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-orange-900 to-red-900 text-orange-200 border-orange-700' 
                  : 'bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 border-orange-200'
              }`}>
                <Calendar size={12} />
                <span>Daily Challenge • {dailyChallengeDate}</span>
                <Flame size={12} />
              </div>
            )}
            
            {/* Messages Area */}
            <MessageList 
              messages={messages} 
              isTyping={isProcessingAI || (gameState === GameState.LOADING_INITIAL)} 
              isThinking={isThinking}
              streamingText={streamingText}
              isDarkMode={isDarkMode}
            />

            {/* Countdown Display - Above Keyboard */}
            {countdown && gameState === GameState.PLAYING && (
              <CountdownDisplay 
                remaining={countdown.remaining}
                label={countdown.label}
                isDarkMode={isDarkMode}
              />
            )}

            {/* Keyboard Area */}
            {gameState === GameState.PLAYING && (
                <PredictiveKeyboard
                    currentInput={currentInput}
                    suggestions={suggestions}
                    isLoadingSuggestions={isLoadingSuggestions && !isProcessingAI}
                    onSelectSuggestion={handleSelectSuggestion}
                    onSend={handleSendMessage}
                    onBackspace={handleBackspace}
                    disabled={isProcessingAI}
                    isDarkMode={isDarkMode}
                />
            )}
        </>
      )}
      </div>
      
      {/* Leaderboard Modal */}
      {showLeaderboard && (
        <Leaderboard 
          isDarkMode={isDarkMode} 
          onClose={() => setShowLeaderboard(false)} 
        />
      )}
      
      {/* Saved Adventures Modal */}
      {showSavedAdventures && (
        <SavedAdventures 
          isDarkMode={isDarkMode} 
          onClose={() => setShowSavedAdventures(false)}
          onLoadAdventure={handleLoadAdventure}
        />
      )}
      
      {/* Username Selection Modal */}
      {showUsernameModal && (
        <UsernameModal 
          onSubmit={handleUsernameSubmit}
          isDarkMode={isDarkMode}
        />
      )}
      
      {/* Login Modal */}
      {showLoginModal && (
        <LoginModal 
          onClose={() => setShowLoginModal(false)}
          onSignIn={handleSignIn}
          isDarkMode={isDarkMode}
        />
      )}
      
      {/* Admin Panel */}
      {showAdminPanel && authToken && (
        <AdminPanel 
          token={authToken}
          onClose={() => setShowAdminPanel(false)}
        />
      )}
      
      {/* Purchase Page */}
      {showPurchasePage && (
        <PurchasePage
          isDarkMode={isDarkMode}
          onClose={() => setShowPurchasePage(false)}
          onPromoRedeemed={refreshUsageData}
        />
      )}
    </>
  );
};

export default App;
